import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  FileText,
  Download,
  ArrowLeft,
  Plus,
  Trash2,
  User,
  Target,
  Briefcase,
  GraduationCap,
  Star,
  Globe,
  CheckCircle,
  Eye,
  Edit3,
  RefreshCw
} from 'lucide-react';
import { useResume } from '../hooks/useResume';
import { mockResumeData, skillLevels, languageLevels, degreeOptions, brazilianStates } from '../data/mock';
import { generatePDF, formatPhoneNumber, validateEmail, validatePhone } from '../utils/pdfGenerator';
import ResumePreview from './ResumePreview';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';

const ResumeEditor = () => {
  const [searchParams] = useSearchParams();
  const showPreviewFirst = searchParams.get('preview') === 'true';
  const [activeTab, setActiveTab] = useState('personal');
  const [showMobilePreview, setShowMobilePreview] = useState(showPreviewFirst);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const previewRef = useRef(null);
  const pdfRef = useRef(null);
  const { toast } = useToast();

  const {
    resumeData,
    isSaved,
    updatePersonalInfo,
    updateObjective,
    addExperience,
    updateExperience,
    removeExperience,
    addEducation,
    updateEducation,
    removeEducation,
    addSkill,
    updateSkill,
    removeSkill,
    addLanguage,
    updateLanguage,
    removeLanguage,
    clearAllData,
    loadMockData
  } = useResume();

  // Load mock data if preview mode
  useEffect(() => {
    if (showPreviewFirst && !resumeData.personalInfo.fullName) {
      loadMockData(mockResumeData);
    }
  }, [showPreviewFirst, loadMockData, resumeData.personalInfo.fullName]);

  const handleGeneratePDF = async () => {
    if (!resumeData.personalInfo.fullName) {
      toast({
        title: 'Dados incompletos',
        description: 'Por favor, preencha pelo menos seu nome completo.',
        variant: 'destructive'
      });
      return;
    }

    setIsGeneratingPDF(true);
    try {
      // Use the hidden full-size preview for PDF generation
      await generatePDF(pdfRef, resumeData.personalInfo.fullName);
      toast({
        title: 'PDF gerado com sucesso!',
        description: 'Seu currículo foi baixado.',
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: 'Erro ao gerar PDF',
        description: 'Tente novamente em alguns instantes.',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    updatePersonalInfo('phone', formatted);
  };

  const tabs = [
    { id: 'personal', label: 'Dados', icon: User },
    { id: 'objective', label: 'Objetivo', icon: Target },
    { id: 'experience', label: 'Experiência', icon: Briefcase },
    { id: 'education', label: 'Formação', icon: GraduationCap },
    { id: 'skills', label: 'Habilidades', icon: Star },
    { id: 'languages', label: 'Idiomas', icon: Globe },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-500 hover:text-slate-700 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-emerald-600" />
              <span className="font-semibold text-slate-800 hidden sm:inline">CurrículoPRO</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 hidden sm:flex items-center gap-1">
              {isSaved ? (
                <><CheckCircle className="w-4 h-4 text-emerald-500" /> Salvo automaticamente</>
              ) : (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Salvando...</>
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden"
              onClick={() => setShowMobilePreview(!showMobilePreview)}
            >
              {showMobilePreview ? <Edit3 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
            <Button
              onClick={handleGeneratePDF}
              disabled={isGeneratingPDF}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Baixar PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Editor Panel */}
          <div className={`${showMobilePreview ? 'hidden lg:block' : 'block'}`}>
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Editor de Currículo</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllData}
                    className="text-slate-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Limpar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid grid-cols-3 sm:grid-cols-6 mb-4 h-auto">
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className="flex flex-col sm:flex-row items-center gap-1 py-2 text-xs sm:text-sm data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
                      >
                        <tab.icon className="w-4 h-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <ScrollArea className="h-[calc(100vh-320px)] pr-4">
                    {/* Personal Info Tab */}
                    <TabsContent value="personal" className="space-y-4 mt-0">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fullName">Nome Completo *</Label>
                          <Input
                            id="fullName"
                            placeholder="João da Silva"
                            value={resumeData.personalInfo.fullName}
                            onChange={(e) => updatePersonalInfo('fullName', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="desiredPosition">Cargo Desejado</Label>
                          <Input
                            id="desiredPosition"
                            placeholder="Desenvolvedor Frontend"
                            value={resumeData.personalInfo.desiredPosition}
                            onChange={(e) => updatePersonalInfo('desiredPosition', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">E-mail *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="seu@email.com"
                            value={resumeData.personalInfo.email}
                            onChange={(e) => updatePersonalInfo('email', e.target.value)}
                            className={resumeData.personalInfo.email && !validateEmail(resumeData.personalInfo.email) ? 'border-red-300' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Telefone *</Label>
                          <Input
                            id="phone"
                            placeholder="(11) 99999-9999"
                            value={resumeData.personalInfo.phone}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            className={resumeData.personalInfo.phone && !validatePhone(resumeData.personalInfo.phone) ? 'border-red-300' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city">Cidade</Label>
                          <Input
                            id="city"
                            placeholder="São Paulo"
                            value={resumeData.personalInfo.city}
                            onChange={(e) => updatePersonalInfo('city', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="state">Estado</Label>
                          <Select
                            value={resumeData.personalInfo.state}
                            onValueChange={(value) => updatePersonalInfo('state', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              {brazilianStates.map((state) => (
                                <SelectItem key={state} value={state}>{state}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Objective Tab */}
                    <TabsContent value="objective" className="space-y-4 mt-0">
                      <div className="space-y-2">
                        <Label htmlFor="objective">Objetivo Profissional</Label>
                        <Textarea
                          id="objective"
                          placeholder="Descreva brevemente seus objetivos de carreira e o que busca na próxima oportunidade..."
                          value={resumeData.objective}
                          onChange={(e) => updateObjective(e.target.value)}
                          rows={5}
                        />
                        <p className="text-xs text-slate-500">
                          Dica: Seja objetivo e mencione a área que deseja atuar.
                        </p>
                      </div>
                    </TabsContent>

                    {/* Experience Tab */}
                    <TabsContent value="experience" className="space-y-4 mt-0">
                      {resumeData.experiences.map((exp) => (
                        <ExperienceCard
                          key={exp.id}
                          experience={exp}
                          onUpdate={updateExperience}
                          onRemove={removeExperience}
                        />
                      ))}
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        onClick={() => addExperience({
                          company: '',
                          position: '',
                          startDate: '',
                          endDate: '',
                          current: false,
                          description: ''
                        })}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Experiência
                      </Button>
                    </TabsContent>

                    {/* Education Tab */}
                    <TabsContent value="education" className="space-y-4 mt-0">
                      {resumeData.education.map((edu) => (
                        <EducationCard
                          key={edu.id}
                          education={edu}
                          onUpdate={updateEducation}
                          onRemove={removeEducation}
                        />
                      ))}
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        onClick={() => addEducation({
                          institution: '',
                          course: '',
                          degree: '',
                          startDate: '',
                          endDate: '',
                          current: false
                        })}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Formação
                      </Button>
                    </TabsContent>

                    {/* Skills Tab */}
                    <TabsContent value="skills" className="space-y-4 mt-0">
                      {resumeData.skills.map((skill) => (
                        <SkillCard
                          key={skill.id}
                          skill={skill}
                          onUpdate={updateSkill}
                          onRemove={removeSkill}
                        />
                      ))}
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        onClick={() => addSkill({ name: '', level: '' })}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Habilidade
                      </Button>
                    </TabsContent>

                    {/* Languages Tab */}
                    <TabsContent value="languages" className="space-y-4 mt-0">
                      {resumeData.languages.map((lang) => (
                        <LanguageCard
                          key={lang.id}
                          language={lang}
                          onUpdate={updateLanguage}
                          onRemove={removeLanguage}
                        />
                      ))}
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        onClick={() => addLanguage({ name: '', level: '' })}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Idioma
                      </Button>
                    </TabsContent>
                  </ScrollArea>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className={`${showMobilePreview ? 'block' : 'hidden lg:block'}`}>
            <Card className="bg-white shadow-sm sticky top-20">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Pré-visualização
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <ScrollArea className="h-[calc(100vh-240px)]">
                  <div className="transform scale-[0.52] origin-top-left" style={{ width: '794px' }}>
                    <ResumePreview ref={previewRef} data={resumeData} />
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Hidden PDF Preview for high quality export */}
        <div className="fixed left-[-9999px] top-0 pointer-events-none" style={{ opacity: 1 }}>
          <ResumePreview ref={pdfRef} data={resumeData} />
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-400 py-6 px-4 mt-auto">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm">
            Criado por{' '}
            <a
              href="https://www.youtube.com/@godgamestutorial"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              God Games Tutorial
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

// Sub-components
const ExperienceCard = ({ experience, onUpdate, onRemove }) => (
  <Card className="border border-slate-200">
    <CardContent className="pt-4 space-y-3">
      <div className="flex justify-between items-start">
        <h4 className="font-medium text-slate-700">Experiência</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(experience.id)}
          className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          placeholder="Empresa"
          value={experience.company}
          onChange={(e) => onUpdate(experience.id, 'company', e.target.value)}
        />
        <Input
          placeholder="Cargo"
          value={experience.position}
          onChange={(e) => onUpdate(experience.id, 'position', e.target.value)}
        />
        <Input
          placeholder="Início (ex: 01/2023)"
          value={experience.startDate}
          onChange={(e) => onUpdate(experience.id, 'startDate', e.target.value)}
        />
        <div className="flex gap-2">
          <Input
            placeholder="Término"
            value={experience.current ? '' : experience.endDate}
            onChange={(e) => onUpdate(experience.id, 'endDate', e.target.value)}
            disabled={experience.current}
            className="flex-1"
          />
          <label className="flex items-center gap-1 text-sm text-slate-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={experience.current}
              onChange={(e) => onUpdate(experience.id, 'current', e.target.checked)}
              className="rounded"
            />
            Atual
          </label>
        </div>
      </div>
      <Textarea
        placeholder="Descreva suas atividades e conquistas..."
        value={experience.description}
        onChange={(e) => onUpdate(experience.id, 'description', e.target.value)}
        rows={2}
      />
    </CardContent>
  </Card>
);

const EducationCard = ({ education, onUpdate, onRemove }) => (
  <Card className="border border-slate-200">
    <CardContent className="pt-4 space-y-3">
      <div className="flex justify-between items-start">
        <h4 className="font-medium text-slate-700">Formação</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(education.id)}
          className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          placeholder="Instituição"
          value={education.institution}
          onChange={(e) => onUpdate(education.id, 'institution', e.target.value)}
        />
        <Input
          placeholder="Curso"
          value={education.course}
          onChange={(e) => onUpdate(education.id, 'course', e.target.value)}
        />
        <Select
          value={education.degree}
          onValueChange={(value) => onUpdate(education.id, 'degree', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Grau" />
          </SelectTrigger>
          <SelectContent>
            {degreeOptions.map((degree) => (
              <SelectItem key={degree} value={degree}>{degree}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input
            placeholder="Início"
            value={education.startDate}
            onChange={(e) => onUpdate(education.id, 'startDate', e.target.value)}
            className="flex-1"
          />
          <Input
            placeholder="Término"
            value={education.current ? '' : education.endDate}
            onChange={(e) => onUpdate(education.id, 'endDate', e.target.value)}
            disabled={education.current}
            className="flex-1"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={education.current}
          onChange={(e) => onUpdate(education.id, 'current', e.target.checked)}
          className="rounded"
        />
        Cursando atualmente
      </label>
    </CardContent>
  </Card>
);

const SkillCard = ({ skill, onUpdate, onRemove }) => (
  <Card className="border border-slate-200">
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Ex: JavaScript, Excel, Gestão..."
          value={skill.name}
          onChange={(e) => onUpdate(skill.id, 'name', e.target.value)}
          className="flex-1"
        />
        <Select
          value={skill.level}
          onValueChange={(value) => onUpdate(skill.id, 'level', value)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Nível" />
          </SelectTrigger>
          <SelectContent>
            {skillLevels.map((level) => (
              <SelectItem key={level} value={level}>{level}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(skill.id)}
          className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

const LanguageCard = ({ language, onUpdate, onRemove }) => (
  <Card className="border border-slate-200">
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Ex: Inglês, Espanhol..."
          value={language.name}
          onChange={(e) => onUpdate(language.id, 'name', e.target.value)}
          className="flex-1"
        />
        <Select
          value={language.level}
          onValueChange={(value) => onUpdate(language.id, 'level', value)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Nível" />
          </SelectTrigger>
          <SelectContent>
            {languageLevels.map((level) => (
              <SelectItem key={level} value={level}>{level}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(language.id)}
          className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

export default ResumeEditor;
